import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { Spinner } from '@/components/common/Spinner';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { usePermission } from '@/permissions/usePermission';
import { swapFreeControlApi } from '@/api/services/swapFreeControl';
import { AddConfigTab } from './tabs/AddConfigTab';
import { ConfigurationsTab } from './tabs/ConfigurationsTab';
import { HistoryTab } from './tabs/HistoryTab';
import './SwapFreeControl.css';

export function SwapFreeControlPage() {
  const can = usePermission();

  const canAdd = can('User.SwapFreeControl.AddConfiguration');
  const canView = can('User.SwapFreeControl.ViewConfiguration');
  const canHistory = can('User.SwapFreeControl.HistoryTab');
  const canEdit = can('User.SwapFreeControl.EditConfiguration');
  const canDelete = can('User.SwapFreeControl.DeleteConfiguration');

  const options = useQuery({ queryKey: ['swap-free', 'options'], queryFn: swapFreeControlApi.getOptions });

  const tabs = useMemo<TabItem[]>(() => {
    const t: TabItem[] = [];
    if (canAdd) t.push({ key: 'add', title: 'Add Config' });
    if (canView) t.push({ key: 'configuration', title: 'Configuration' });
    if (canHistory) t.push({ key: 'history', title: 'History' });
    return t;
  }, [canAdd, canView, canHistory]);

  const [active, setActive] = useState<string>('');
  const activeKey = active || tabs[0]?.key || '';

  if (options.isLoading) return <Spinner />;

  const opts = options.data ?? { directions: [], calculationTypes: [], statuses: [], types: [] };

  return (
    <div className="swap-free-control-page">
      <PageHeader title="Swap Free Control" breadcrumb={['Home', 'Swap Free Control']} />

      <div className="card custom-card mb-0">
        <div className="card-header p-0">
          <Tabs tabs={tabs} active={activeKey} onChange={setActive} />
        </div>
        <div className="card-body">
          {activeKey === 'add' && canAdd && (
            <AddConfigTab options={opts} onSaved={() => setActive('configuration')} />
          )}
          {activeKey === 'configuration' && canView && (
            <ConfigurationsTab
              options={opts}
              canEdit={canEdit}
              canDelete={canDelete}
              canAdd={canAdd}
              onGoToAddConfig={() => setActive('add')}
            />
          )}
          {activeKey === 'history' && canHistory && <HistoryTab />}
          {tabs.length === 0 && <p className="text-muted mb-0">You don't have access to any Swap Free Control tabs.</p>}
        </div>
      </div>
    </div>
  );
}
